// util.ts — tiny helpers shared across pages.

/** Escape a string for embedding inside HTML text or attribute values. */
export function esc(s: unknown): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Tagged template that escapes interpolated values by default. Pass
 *  raw(...) to opt out for an already-trusted HTML fragment. */
export function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  let out = '';
  for (let i = 0; i < strings.length; i++) {
    out += strings[i];
    if (i < values.length) {
      const v = values[i];
      if (v && typeof v === 'object' && (v as { __raw?: string }).__raw !== undefined) {
        out += (v as { __raw: string }).__raw;
      } else if (Array.isArray(v)) {
        out += v.join('');
      } else if (v === null || v === undefined || v === false) {
        out += '';
      } else {
        out += esc(v);
      }
    }
  }
  return out;
}

/** Mark a string as already-safe HTML so the html`` tag doesn't escape it. */
export function raw(s: string): { __raw: string } { return { __raw: s }; }

/** Compose multiple html`` results into one (typesafe sugar). */
export function frag(parts: Array<string | { __raw: string }>): { __raw: string } {
  return raw(parts.map(p => typeof p === 'string' ? p : p.__raw).join(''));
}

/** First-initial of first token + first-initial of last token. */
export function initialsOf(fullName: string | null | undefined): string {
  if (!fullName) return '?';
  const parts = String(fullName).trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Format an integer dollar amount with thousands separators. */
export function fmtMoney(n: number | string | null | undefined): string {
  const v = Number(n || 0);
  return '$' + v.toLocaleString();
}

/** Currency or em-dash for missing values. */
export function fmtMoneyOrDash(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '\u2014';
  return fmtMoney(n);
}

/** "Aug 21, 2024" or em-dash. */
export function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Aug 21, 2024 · 3:42 PM" or em-dash. */
export function fmtDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** "3 minutes ago" / "2 hours ago" / "5 days ago" / "1 week ago". */
export function fmtRelative(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const t = new Date(dateStr).getTime();
  if (!Number.isFinite(t)) return '\u2014';
  const diff  = Date.now() - t;
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  if (mins  <  1) return 'just now';
  if (mins  < 60) return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days  <  7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
}

/** Byte count → "12 B" / "847 KB" / "3.2 MB" / em-dash. */
export function fmtFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** First token of a name, lowercased acceptable, fallback to 'there'. */
export function firstNameOf(fullName: string | null | undefined): string {
  if (!fullName) return 'there';
  return String(fullName).trim().split(/\s+/)[0] || 'there';
}

/** Live-status vocabulary → app.css pill class suffix. */
export function statusPillClass(status: string | null | undefined): string {
  const map: Record<string, string> = {
    Approved:           'pill-approved',
    Pending:            'pill-pending',
    'Under Review':     'pill-review',
    Submitted:          'pill-submitted',
    'Action Required':  'pill-action',
    Denied:             'pill-denied',
    Draft:              'pill-draft',
  };
  return map[status || ''] || '';
}

/** Status → color CSS variable, used by activity timelines. */
export function statusColor(status: string | null | undefined): string {
  if (status === 'Approved')                                  return 'var(--st-approved)';
  if (status === 'Denied' || status === 'Action Required')    return 'var(--st-denied)';
  if (status === 'Under Review')                              return 'var(--accent)';
  if (status === 'Submitted')                                 return 'var(--st-review)';
  return 'var(--ink-mute)';
}

/** Right-arrow inline SVG, sized to match the surrounding text. */
export function arrowR(size = 16): string {
  return html`<svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 8 L13 8 M9 4 L13 8 L9 12" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

/** Sprout mark used inside the brand badge. */
export function markGlyph(size = 38): string {
  const inner = size * 0.55;
  return html`<span class="mark" style="width:${size}px;height:${size}px;">
    <svg width="${inner}" height="${inner}" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21 V11" stroke="var(--paper)" stroke-width="1.8" stroke-linecap="round"/>
      <path d="M12 11 C8 11 6 9 6 6 C9.5 6 12 8 12 11 Z" fill="var(--paper)"/>
      <path d="M12 11 C16 11 18 8 18 5 C14.5 5 12 7 12 11 Z" fill="var(--paper)"/>
    </svg>
  </span>`;
}

/** Map a DB role string ('User'|'Approver'|'Admin') to the lowercase
 *  UI role used for gating ('public'|'user'|'approver'|'admin'). */
export function roleToUiRole(role: string | undefined | null): 'public' | 'user' | 'approver' | 'admin' {
  if (role === 'Admin')    return 'admin';
  if (role === 'Approver') return 'approver';
  if (role === 'User')     return 'user';
  return 'public';
}
